import { Valence } from '@upright/common/model/types';
import { formatNumberEfficiently, NumberType } from '@upright/ui/common/generic/utilities/format-numbers-efficiently';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { PlainIconFigureBox } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/figure-box';
import HouseIcon from '@upright/ui/icons/img/streamline-light/building-construction/houses/house-1.svg';
import Co2Icon from '@upright/ui/icons/img/streamline-light/ecology/pollution/pollution-co-2.svg';
import { useMoneyFiguresForSelection } from '../monetary/monetary-figure-boxes';

function ghgUnitsComparisons(dollarsPerYear: number) {
  const carbonPrice = 417;
  // https://www.ccfpd.org/Portals/0/Assets/PDF/Facts_Chart.pdf
  const householdAverageGhgTons = 7.5;
  const tonsPerYear = dollarsPerYear / carbonPrice;
  const householdProductionPerYear = tonsPerYear / householdAverageGhgTons;
  return { tonsPerYear, householdProductionPerYear };
}

export function useGhgComparisons() {
  const context = useNetImpactLensContext();
  const { dollarsPerYear } = useMoneyFiguresForSelection();
  const valence = context?.state.valence;
  if (!dollarsPerYear || !valence) {
    return [];
  }
  const { householdProductionPerYear, tonsPerYear } = ghgUnitsComparisons(dollarsPerYear);

  const formattedValue = formatNumberEfficiently(tonsPerYear, NumberType.GENERIC, 3);

  const tonBox = (
    <PlainIconFigureBox
      icon={Co2Icon}
      numberText={formattedValue}
      textBelowNumber="tons of GHG emissions"
      explanationText={getGhgExplanationText(valence, formattedValue)}
    />
  );

  const householdFormattedValue = formatNumberEfficiently(householdProductionPerYear, NumberType.GENERIC, 3);
  const householdBox = (
    <PlainIconFigureBox
      icon={HouseIcon}
      numberText={householdFormattedValue}
      textBelowNumber="households"
      explanationText={
        <>
          ...which equals the average annual emissions of <b>{householdFormattedValue}</b> U.S. households.
        </>
      }
    />
  );

  return [tonBox, householdBox];
}

function getGhgExplanationText(valence: Valence, formattedValue: string) {
  if (valence === Valence.NEGATIVE) {
    return (
      <>
        This company's estimated share<sup>1</sup> of global GHG emissions is <b>{formattedValue}</b> tons per year...
      </>
    );
  } else {
    return (
      <>
        This company's positive GHG emissions impact is <b>{formattedValue}</b> tons per year...
      </>
    );
  }
}
